import { simplifyAddress } from '../components/AddressLookup/address';
import {
  BoxCount,
  Discount,
  MakeOrderRequest,
  MakeSuitcaseOrderRequest,
} from '../models/APIModels';
import { PaymentState, RemovalOrderRequest } from '../types/payment';

/**
 * The order-creation request bodies.
 *
 * `Payment.tsx` built these by hand nine times — three shapes across the
 * card path, the Klarna path and the Apple Pay handler — and the copies
 * differed only in how the payer is identified. That is the API contract
 * for taking money written out nine times: adding a field to the card path
 * and missing the Klarna one produces an order that is subtly wrong only
 * for customers who chose the other button.
 */

/**
 * How this request identifies the payer. The card paths carry a Stripe
 * payment-method id; Klarna is confirmed after a redirect, so the order is
 * created with the method type instead.
 */
export type PaymentIdentity =
  | { paymentMethodId: string }
  | { paymentMethodType: 'klarna' };

/** The address-and-date fields both catalogue orders share. */
function journeyFields(
  state: PaymentState,
  flow: string,
  discount?: Discount,
) {
  return {
    city: state.collectionAddress.city,
    collectionAddress: simplifyAddress(state.collectionAddress),
    collectionDate: state.collectionDate.getTime(),
    deliveryAddress:
      state.deliveryAddress && simplifyAddress(state.deliveryAddress),
    deliveryDate: state.deliveryDate?.getTime(),
    discountCode: discount?.discountCode,
    note: flow,
    collectionCountry: state.collectionAddress.country,
    collectionPostcode: state.collectionAddress.postCode,
    deliveryCountry: state.deliveryAddress?.country,
    deliveryPostcode: state.deliveryAddress?.postCode,
  };
}

export function buildBoxOrderRequest(
  state: PaymentState,
  flow: string,
  identity: PaymentIdentity,
  discount?: Discount,
): MakeOrderRequest {
  return {
    ...journeyFields(state, flow, discount),
    ...identity,
    boxes: state.boxes.reduce((acc, each) => {
      acc[each.id] = each.count;
      return acc;
    }, {} as BoxCount),
    roomPack: false,
    storageRequired: state.storageRequired,
  };
}

export function buildSuitcaseOrderRequest(
  state: PaymentState,
  flow: string,
  identity: PaymentIdentity,
  discount?: Discount,
): MakeSuitcaseOrderRequest {
  return {
    ...journeyFields(state, flow, discount),
    ...identity,
    suitcases: state.suitcases ?? [],
    country: state.collectionAddress.country,
  };
}

/**
 * Removals orders describe a move rather than a set of boxes, so they carry
 * their own field names (`collectionCity`, `moveDate`) and default an
 * absent country to the UK rather than sending it undefined.
 *
 * The discount is attached only when there is one. The Klarna copy used to
 * spread `discountCode: undefined` into the literal instead — equivalent on
 * the wire, since `JSON.stringify` drops undefined values, but only by
 * accident.
 */
export function buildRemovalsOrderRequest(
  state: PaymentState,
  flow: string,
  identity: PaymentIdentity,
  discount?: Discount,
): RemovalOrderRequest {
  const request: RemovalOrderRequest = {
    tierId: state.selectedTier.id,
    tierName: state.selectedTier.name,
    tierPrice: state.selectedTier.price,
    propertyQuestions: {
      floor: state.questions.floor,
      hasLift: state.questions.hasLift,
      hasFurniture: state.questions.hasFurniture,
      hasDisassembly: state.questions.hasDisassembly,
      hasParking: state.questions.hasParking,
    },
    collectionAddress: simplifyAddress(state.collectionAddress),
    collectionPostcode: state.collectionAddress.postCode || '',
    collectionCity: state.collectionAddress.city || '',
    collectionCountry: state.collectionAddress.country || 'United Kingdom',
    deliveryAddress: state.deliveryAddress
      ? simplifyAddress(state.deliveryAddress)
      : '',
    deliveryPostcode: state.deliveryAddress?.postCode || '',
    deliveryCity: state.deliveryAddress?.city || '',
    deliveryCountry: state.deliveryAddress?.country || 'United Kingdom',
    moveDate: state.moveDate!.getTime(),
    note: flow,
    ...identity,
  };

  if (state.wantsPacking && state.selectedPacking) {
    request.packingMaterials = {
      id: state.selectedPacking.id,
      name: state.selectedPacking.name,
      price: state.selectedPacking.price,
      items: state.selectedPacking.items,
      rrp: state.selectedPacking.rrp,
    };
  }

  if (state.wantsStorage && state.selectedStorage) {
    request.storage = {
      id: state.selectedStorage.id,
      name: state.selectedStorage.name,
      size: state.selectedStorage.size,
      price: state.selectedStorage.price,
    };
  }

  if (discount?.discountCode) {
    request.discountCode = discount.discountCode;
  }

  return request;
}
